/**
 * SuggestionCard - AI insight/recommendation card with confidence score
 */

import { motion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { AISuggestion } from './types'
import { getSuggestionStyles } from './types'

interface SuggestionCardProps {
  suggestion: AISuggestion
  onAction?: (action: string, data: any) => void
}

export function SuggestionCard({ suggestion, onAction }: SuggestionCardProps) {
  const styles = getSuggestionStyles(suggestion.type)
  const Icon = styles.icon

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('p-3 rounded-xl border transition-all duration-200', styles.bgColor, styles.borderColor, 'hover:shadow-sm')}
    >
      <div className="flex items-start gap-3">
        <div className={cn('p-1.5 rounded-lg', styles.textColor)}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-slate-900 dark:text-white">{suggestion.title}</h4>
            <span className="text-[10px] text-slate-400 font-mono">{Math.round(suggestion.confidence * 100)}%</span>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{suggestion.description}</p>
          {suggestion.action && (
            <button
              onClick={() => {
                if (suggestion.action?.handler) suggestion.action.handler()
                else if (suggestion.action?.url) window.location.href = suggestion.action.url
                onAction?.('suggestion_action', suggestion)
              }}
              className={cn('mt-2 inline-flex items-center gap-1 text-xs font-medium transition-colors', styles.textColor, 'hover:underline')}
            >
              {suggestion.action.label}
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
